import { isNumber, isString } from "../utils/schemaUtils";
import { type GitHubIssue, isGitHubIssue } from "./githubIssuesSchema";

export interface GitHubReview {
  reviewer: string;
  state: string;
  body?: string;
  submittedAt: string;
}

const isGitHubReview = (v: unknown): v is GitHubReview => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.reviewer) && isString(r.state) && (r.body === undefined || isString(r.body)) && isString(r.submittedAt)
  );
};

// `mergeableState` is GitHub's own rollup over conflicts, branch protection and
// required checks, and reads "unknown" while GitHub is still computing it rather
// than reporting a merge as blocked.
export interface GitHubPullRequestPayload extends GitHubIssue {
  type: "github_pull_request";
  body?: string;
  draft: boolean;
  merged: boolean;
  mergeableState?: string;
  baseRef: string;
  headRef: string;
  additions: number;
  deletions: number;
  changedFiles: number;
  mergedAt?: string;
  requestedReviewers: string[];
  reviews: GitHubReview[];
}

export const parseGitHubPullRequestPayload = (v: unknown): GitHubPullRequestPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "github_pull_request") return null;
  if (!isGitHubIssue(r)) return null;
  if (r.body !== undefined && !isString(r.body)) return null;
  if (typeof r.draft !== "boolean" || typeof r.merged !== "boolean") return null;
  if (r.mergeableState !== undefined && !isString(r.mergeableState)) return null;
  if (!isString(r.baseRef) || !isString(r.headRef)) return null;
  if (!isNumber(r.additions) || !isNumber(r.deletions) || !isNumber(r.changedFiles)) return null;
  if (r.mergedAt !== undefined && !isString(r.mergedAt)) return null;
  if (!Array.isArray(r.requestedReviewers) || !r.requestedReviewers.every(isString)) return null;
  if (!Array.isArray(r.reviews) || !r.reviews.every(isGitHubReview)) return null;
  const issue: GitHubIssue = r;
  return {
    ...issue,
    type: "github_pull_request",
    body: r.body,
    draft: r.draft,
    merged: r.merged,
    mergeableState: r.mergeableState,
    baseRef: r.baseRef,
    headRef: r.headRef,
    additions: r.additions,
    deletions: r.deletions,
    changedFiles: r.changedFiles,
    mergedAt: r.mergedAt,
    requestedReviewers: r.requestedReviewers,
    reviews: r.reviews,
  };
};
